import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { __ } from '../../../../i18n/l10n';
import theme from '../../../../styled-components/dark';
import { extractHexColor, getColorFromPercentage } from '../../../../utils/color';
import MissionBoxWrapper from '../common/MissionBoxWrapper';

const progressBackgroundColorRange = [
  {
    percentRatio: 0,
    color: {
      r: extractHexColor(theme.MissionTimer.progressBackground.step1, 'r'),
      g: extractHexColor(theme.MissionTimer.progressBackground.step1, 'g'),
      b: extractHexColor(theme.MissionTimer.progressBackground.step1, 'b'),
    },
  },
  {
    percentRatio: 0.33,
    color: {
      r: extractHexColor(theme.MissionTimer.progressBackground.step2, 'r'),
      g: extractHexColor(theme.MissionTimer.progressBackground.step2, 'g'),
      b: extractHexColor(theme.MissionTimer.progressBackground.step2, 'b'),
    },
  },
  {
    percentRatio: 0.67,
    color: {
      r: extractHexColor(theme.MissionTimer.progressBackground.step3, 'r'),
      g: extractHexColor(theme.MissionTimer.progressBackground.step3, 'g'),
      b: extractHexColor(theme.MissionTimer.progressBackground.step3, 'b'),
    },
  },
  {
    percentRatio: 1,
    color: {
      r: extractHexColor(theme.MissionTimer.progressBackground.step4, 'r'),
      g: extractHexColor(theme.MissionTimer.progressBackground.step4, 'g'),
      b: extractHexColor(theme.MissionTimer.progressBackground.step4, 'b'),
    },
  },
];

const OverflowWrapper = styled.div`
  border-radius: 0.5em;
  overflow: hidden;
`;

const Wrapper = styled(MissionBoxWrapper)`
  overflow: visible;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 6em;
  padding: 0.3em 0.8em;
  border-radius: 0.5em;

  transform-style: preserve-3d;
`;

const Timer = styled.div`
  display: flex;

  color: white;
  font-weight: bold;
  font-size: 90%;
  transform: translateZ(1px);
`;

type FilledBackgroundType = {
  $ratio?: number;
  $isFinished: boolean;
};

const FilledBackground = styled.div<FilledBackgroundType>`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;

  ${({ $ratio, $isFinished }) => css`
    width: ${$ratio ? 100 - $ratio : 0}%;
    background-color: ${$isFinished
      ? theme.MissionTimer.progressBackground.finished
      : getColorFromPercentage($ratio, progressBackgroundColorRange)};
  `}

  opacity: 0.9;
  transform: translateZ(-1px);
`;

type RemainingTimeType = {
  days: number;
  hours: number;
  mins: number;
  diff?: number;
};

type MissionTimerProps = {
  startDate?: number;
  endDate?: number;
};

const MissionTimer = ({ startDate, endDate }: MissionTimerProps) => {
  const [remainingTime, setRemainingTime] = useState<RemainingTimeType>();
  const { days, hours, mins, diff } = remainingTime ?? {};
  const duration = useMemo(() => (endDate && startDate ? endDate - startDate : 0), [endDate, startDate]);
  const remainingTimeRatio = useMemo(
    () => Math.max(0, Math.min(diff !== undefined ? diff / duration : 0, 1)),
    [diff, duration],
  );

  const remainingTimeRatioRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    remainingTimeRatioRef.current = remainingTimeRatio;
  }, [remainingTimeRatio]);

  const intervalPid = useRef<NodeJS.Timeout | null>(null);

  useLayoutEffect(() => {
    if (intervalPid.current) {
      clearInterval(intervalPid.current);
      intervalPid.current = null;
    }

    if (startDate && endDate && !intervalPid.current) {
      const intervalCallback = () => {
        const now = Date.now();
        const remainingTimeInMs = endDate - now;

        const remainingDays = remainingTimeInMs / 1000 / 3600 / 24;
        const remainingHours = (remainingDays % 1) * 24;
        const remainingMins = (remainingHours % 1) * 60;

        setRemainingTime({
          diff: Date.now() - startDate,
          days: Math.max(0, Math.floor(remainingDays)),
          hours: Math.max(0, Math.floor(remainingHours)),
          mins: Math.max(0, Math.floor(remainingMins)),
        });
      };

      intervalPid.current = setInterval(intervalCallback, 1000);
      intervalCallback();
    }
  }, [startDate, endDate]);

  useLayoutEffect(() => {
    return () => {
      if (intervalPid.current) {
        clearInterval(intervalPid.current);
        intervalPid.current = null;
      }
    };
  }, []);

  useLayoutEffect(() => {
    if (diff !== undefined && diff <= 0 && intervalPid.current) {
      clearInterval(intervalPid.current);
      intervalPid.current = null;
    }
  }, [diff]);

  const shouldRender = useMemo(
    () =>
      startDate &&
      endDate &&
      startDate < endDate &&
      typeof days === 'number' &&
      typeof hours === 'number' &&
      typeof mins === 'number',
    [days, endDate, hours, mins, startDate],
  );

  const timerStr = useMemo(() => {
    if (days === 0 && hours === 0 && mins === 0) {
      return __('timer:finished');
    } else {
      let timeString = '';
      if (days !== undefined && days > 0) {
        timeString += `${days}${__('timer:d')} `;
      }

      if (days !== undefined && hours !== undefined && (days > 0 || hours > 0)) {
        timeString += `${String(hours).padStart(2, '0')}${__('timer:h')} `;
      }

      if (days === 0) {
        timeString += `${String(mins).padStart(2, '0')}${__('timer:min')}`;
      }

      return timeString;
    }
  }, [days, hours, mins]);

  return shouldRender ? (
    <OverflowWrapper>
      <Wrapper>
        <FilledBackground $ratio={remainingTimeRatio * 100} $isFinished={days === 0 && hours === 0 && mins === 0} />
        <Timer>{timerStr}</Timer>
      </Wrapper>
    </OverflowWrapper>
  ) : null;
};

export default MissionTimer;
